/**
 * System bridge: submit **restriction intent** to native code (FamilyControls / ManagedSettings / DeviceActivity).
 *
 * ## Invariant
 * - JS **declares intent** only. It does not “block apps.”
 * - Apple (or the OS) **enforces** within Screen Time–style APIs, user approval, and system timing.
 *
 * ## Identity vs enforcement (iOS)
 * - Swift consumes **only** `plan.restrictTargets` / `plan.allowTargets` (`lockbox_app`, `application_token_ref`).
 * - **`ApplicationToken` never crosses the JS bridge.** Persist tokens only in Swift / app group; expose opaque refs to JS if needed.
 * - `plan.*BundleHints` are for simulator / labels — **ignore** for `ManagedSettingsStore.shield.applications`.
 */

/** @typedef {import('../domain/types.js').EnforcementPlan} EnforcementPlan */

/**
 * @typedef {Object} EnforcementAdapter
 * @property {(plan: EnforcementPlan) => Promise<{ ok: boolean, error?: string, skipped?: string }>} submitIntent
 */

/** @type {EnforcementAdapter} */
let adapter = {
  async submitIntent(_plan) {
    return { ok: true, skipped: "noop_adapter" };
  },
};

/**
 * @param {EnforcementAdapter | { applyPlan: EnforcementAdapter['submitIntent'] }} next
 */
export function registerEnforcementAdapter(next) {
  if (!next) {
    throw new Error("registerEnforcementAdapter: invalid adapter");
  }
  if (typeof next.submitIntent === "function") {
    adapter = /** @type {EnforcementAdapter} */ (next);
    return;
  }
  // Legacy name: applyPlan === submitIntent
  if (typeof next.applyPlan === "function") {
    adapter = { submitIntent: next.applyPlan.bind(next) };
    return;
  }
  throw new Error("registerEnforcementAdapter: adapter must implement submitIntent(plan)");
}

/**
 * Preferred API name: submit restriction intent to the system bridge.
 * @param {EnforcementPlan} plan
 */
export async function submitRestrictionIntent(plan) {
  return adapter.submitIntent(plan);
}

/**
 * @deprecated Use {@link submitRestrictionIntent}. Kept for readable grep during migration.
 * @param {EnforcementPlan} plan
 */
export async function applyEnforcementPlan(plan) {
  return submitRestrictionIntent(plan);
}

export function getEnforcementAdapterDebug() {
  return "noop_or_registered";
}
